<template>
  <PortalLayout section="Tenants">
    <div class="mt-4">
      <div v-if="isPropertyLoading"><Loading /></div>
      <div v-else>
        <div v-if="tenants && tenants.length > 0">
          <span class="w-full flex my-2" v-if="tenantOrderChanged">
            <p class="grow text-red-700">
              Your sort order changes are unsaved, when done sorting, please hit the 'Save Changes'
              button to save
            </p>
            <SaveButton @click="saveSortedTenants" />
          </span>
          <AddButton @click="addClick">Add Tenant</AddButton>
          <AddButton class="sm:float-right" @click="bulkAddClick"> Bulk Add Tenants </AddButton>
          <div v-for="tenant in tenants" :key="tenant.name" class="my-2 bg-gray-100 border-t">
            <div class="flex">
              <div class="flex-col mx-2">
                <i-heroicons-outline-chevron-up
                  class="h-5 w-5 mt-1 mb-2"
                  :class="
                    tenant.sortOrder == Math.min(...tenants.map((t) => t.sortOrder))
                      ? 'text-gray-400'
                      : 'cursor-pointer hover:bg-blue-200'
                  "
                  @click="swapTenantSort(tenant.sortOrder, true)"
                />
                <i-heroicons-outline-chevron-down
                  class="h-5 w-5 mb-1"
                  :class="
                    tenant.sortOrder == Math.max(...tenants.map((t) => t.sortOrder))
                      ? 'text-gray-400'
                      : 'cursor-pointer hover:bg-blue-200'
                  "
                  @click="swapTenantSort(tenant.sortOrder, false)"
                />
              </div>
              <div
                class="cursor-pointer hover:bg-blue-200 flex-grow px-4 py-4"
                @click="editClick(tenant)"
              >
                {{ tenant.name }}
                <span v-if="tenant.isRestricted || tenant.isEmailVerificationRequired">
                  (restricted by
                  {{
                    (tenant.isRestricted ? "invite code" : "") +
                    (tenant.isRestricted && tenant.isEmailVerificationRequired ? " and " : "") +
                    (tenant.isEmailVerificationRequired ? "email domin" : "")
                  }})</span
                >
              </div>
            </div>
          </div>
        </div>
        <div v-else class="mb-2">
          <span>-- No Tenants found for this building --</span>
        </div>
        <div>
          <AddButton @click="addClick">Add Tenant</AddButton>
          <EditButton @click="sortAlpha">Re-Sort Alphabetically</EditButton>
          <AddButton
            v-if="!tenants || tenants.length === 0"
            class="sm:float-right"
            @click="bulkAddClick"
          >
            Bulk Add Tenants
          </AddButton>
        </div>
        <EditPopup
          entity-name="Tenant"
          :is-visible="isPopupVisible"
          :is-edit-mode="isEditing"
          :save-error="popupError"
          :delete-enabled="true"
          @close="isPopupVisible = false"
          @save="saveClick"
          @delete="deleteClick"
        >
          <BaseInput label="Name" v-model="tenantName" :error="errors.tenantName" />
          <BaseInput
            label="System Name"
            v-model="tenantSystemName"
            :error="errors.tenantSystemName"
          />

          <BaseInput label="VR Launch Url" v-model="vrLaunchLink" :error="errors.vrLaunchLink" />
          <div class="text-sm pl-8 pb-2 text-gray-400">
            Note: a <span class="font-mono text-blue-400">t=(SystemName)</span> will be added to url
            unless specified
          </div>
          <BaseInput
            label="Sso Match Values"
            v-model="ssoMatchValues"
            :error="errors.ssoMatchValues"
          />
          <BaseInput
            class="mb-4"
            label="Email Domain Match"
            v-model="emailDomainMatch"
            :error="errors.emailDomainMatch"
          />
          <BaseCheckbox label="Sso Default" v-model="isSsoDefault" :error="errors.isSsoDefault" />
          <BaseCheckbox
            label="Requires Invite Code"
            v-model="isRestricted"
            :error="errors.isRestricted"
          />
          <BaseCheckbox
            label="Requires Email Verification"
            v-model="isEmailVerificationRequired"
            :error="errors.isEmailVerificationRequired"
          />
          <BaseCheckbox
            :disabled="!property?.isTrainingEnabled"
            label="Send Training Expiration Emails"
            v-model="sendExpirationEmails"
            :error="errors.sendExpirationEmails"
          />
        </EditPopup>

        <EditPopup
          entity-name="Tenants in Bulk"
          :is-visible="isBulkPopupVisible"
          :is-edit-mode="false"
          :save-error="popupError"
          :delete-enabled="false"
          :saveOnEnter="false"
          :close-on-outside-click="false"
          @close="isBulkPopupVisible = false"
          @save="saveBulkClick"
        >
          <p class="font-medium mb-4">Each tenant should be entered on its own line.</p>
          <BaseTextarea
            class="h-64 mb-8"
            label="Tenant List"
            v-model="bulkTenantList"
            :allowResize="false"
            :error="errorsBulk.bulkTenantList"
          />
        </EditPopup>
      </div>
    </div>
  </PortalLayout>
</template>

<script setup lang="ts">
import { useBldgStore } from "../../store/bldgStore";
import { BulkTenantRequest, SaveTenantCommand, TenantDTO } from "../../store/generatedFetchClient";

import { useField, useForm } from "vee-validate";
import * as yup from "yup";

const bldgStore = useBldgStore();

var isPropertyLoading = computed(() => bldgStore.isLoading);
var property = computed(() => bldgStore.property);
var tenants = ref([] as TenantDTO[]);

onMounted(loadData);
watch(property, loadData);

function loadData() {
  if (property.value !== null) {
    // Assign fresh sortOrder values based off the current order (in case of unmigrated)
    let newSortValue = 0;
    tenants.value = bldgStore.property?.tenants.map((tenant) => ({
      ...tenant,
      sortOrder: newSortValue++,
    }));
  }
}

let popupError = ref("");
function onInvalidSubmit(ctx: any) {
  popupError.value = "One or more invalid form inputs.";
  console.log("error trying to submit form", ctx);
}

// Add Popup ---------------------------

let isPopupVisible = ref(false);
let isEditing = ref(false);

const { handleSubmit, errors } = useForm({
  validationSchema: yup.object({
    tenantName: yup.string().required().max(100).label("Name"),
    tenantSystemName: yup.string().required().max(100).label("System Name"),
    vrLaunchLink: yup.string().notRequired().max(255).label("VR Launch Url"),
    ssoMatchValues: yup.string().notRequired().max(255).label("Sso Match Values"),
    emailDomainMatch: yup.string().notRequired().max(50).label("Email Domain Match"),
    isSsoDefault: yup.boolean().notRequired().label("Default for SSO Login"),
    isRestricted: yup.boolean().notRequired().label("Hidden on registration form"),
    sendExpirationEmails: yup.boolean().notRequired().label("Send Expiration Emails"),
  }),
  initialValues: {
    tenantName: "",
    tenantSystemName: "",
    vrLaunchLink: "",
    ssoMatchValues: "",
    emailDomainMatch: "",
    isSsoDefault: false,
    isRestricted: false,
    isEmailVerificationRequired: false,
    sendExpirationEmails: true,
  },
});

let tenantId = ref(0);
const { value: tenantName } = useField<string>("tenantName");
const { value: tenantSystemName } = useField<string>("tenantSystemName");
const { value: vrLaunchLink } = useField<string>("vrLaunchLink");
const { value: ssoMatchValues } = useField<string>("ssoMatchValues");
const { value: isSsoDefault } = useField<boolean>("isSsoDefault");
const { value: isRestricted } = useField<boolean>("isRestricted");
const { value: isEmailVerificationRequired } = useField<boolean>("isEmailVerificationRequired");
const { value: sendExpirationEmails } = useField<boolean>("sendExpirationEmails");
let sortOrder = ref(-1);
const { value: emailDomainMatch } = useField<string>("emailDomainMatch");

let saveClick = handleSubmit(onValidSubmit, onInvalidSubmit);

async function onValidSubmit() {
  let saveTenant = {
    propertyId: property.value?.propertyId,
    tenantId: tenantId.value ?? 0,
    name: tenantName.value,
    systemName: tenantSystemName.value,
    vrLaunchUrl: vrLaunchLink.value ?? "",
    ssoMatchValues: ssoMatchValues.value ?? "",
    isSsoDefault: isSsoDefault.value ?? false,
    isRestricted: isRestricted.value ?? false,
    sortOrder: Number.isSafeInteger(sortOrder.value) ? sortOrder.value : 0,
    emailDomainMatch: emailDomainMatch.value ?? "",
    isEmailVerificationRequired: isEmailVerificationRequired.value ?? false,
    suppressExpirationEmails: !(sendExpirationEmails.value ?? true),
  } as SaveTenantCommand;

  popupError.value = "";

  console.log("saving tenant", saveTenant);
  if (property.value && property.value.portalId) {
    let possibleError = await bldgStore.saveTenant(saveTenant);
    if (!possibleError) {
      isPopupVisible.value = false;
    } else {
      //handle error
      popupError.value = possibleError;
    }
  }
}

let addClick = function () {
  tenantId.value = 0;
  tenantName.value = "";
  tenantSystemName.value = "";
  vrLaunchLink.value = "";
  ssoMatchValues.value = "";
  isSsoDefault.value = false;
  isRestricted.value = false;
  emailDomainMatch.value = "";
  isEmailVerificationRequired.value = false;
  sendExpirationEmails.value = true;

  if (top) {
    sortOrder.value = Math.min(...tenants.value.map((t) => t.sortOrder)) - 1;
  } else {
    sortOrder.value = Math.max(...tenants.value.map((t) => t.sortOrder)) + 1;
  }

  popupError.value = "";
  isEditing.value = false;
  isPopupVisible.value = true;
};

let editClick = function (selectedTenant: TenantDTO) {
  tenantId.value = selectedTenant.tenantId ?? 0;
  tenantName.value = selectedTenant.name ?? "";
  tenantSystemName.value = selectedTenant.systemName ?? "";
  vrLaunchLink.value = selectedTenant.vrLaunchUrl ?? "";
  ssoMatchValues.value = selectedTenant.ssoMatchValues ?? "";
  isSsoDefault.value = selectedTenant.isSsoDefault ?? false;
  isRestricted.value = selectedTenant.isRestricted ?? false;
  sortOrder.value = selectedTenant.sortOrder;
  emailDomainMatch.value = selectedTenant.emailDomainMatch ?? "";
  isEmailVerificationRequired.value = selectedTenant.isEmailVerificationRequired ?? false;
  sendExpirationEmails.value = !((selectedTenant as any).suppressExpirationEmails ?? false);

  popupError.value = "";
  isEditing.value = true;
  isPopupVisible.value = true;
};

async function deleteClick() {
  if (isEditing.value && tenantId.value > 0) {
    popupError.value = await bldgStore.deleteTenant(tenantId.value);
    if (!popupError.value) isPopupVisible.value = false;
  }
}

// Reordering -------------------------------

var tenantOrderChanged = ref(false);

function swapTenantSort(sortID: number, sortUp: boolean) {
  let selectedTenantID = tenants.value.find((t) => t.sortOrder == sortID).tenantId;

  if (sortUp) {
    let swapTenantID = tenants.value.find((t) => t.sortOrder == sortID - 1).tenantId;
    tenants.value.find((t) => t.tenantId == selectedTenantID).sortOrder = sortID - 1;
    tenants.value.find((t) => t.tenantId == swapTenantID).sortOrder = sortID;

    tenantOrderChanged.value = true;
  } else {
    let swapTenantID = tenants.value.find((t) => t.sortOrder == sortID + 1).tenantId;
    tenants.value.find((t) => t.tenantId == selectedTenantID).sortOrder = sortID + 1;
    tenants.value.find((t) => t.tenantId == swapTenantID).sortOrder = sortID;

    tenantOrderChanged.value = true;
  }

  sortAllTenants();
}

function sortAlpha() {
  tenants.value.sort((x, y) => {
    if (x.name.toLowerCase() < y.name.toLowerCase()) {
      return -1;
    } else if (x.name.toLowerCase() > y.name.toLowerCase()) {
      return 1;
    }
    return 0;
  });

  for (var i = 0; i < tenants.value.length; i++) {
    tenants.value[i].sortOrder = i;
  }
  tenantOrderChanged.value = true;
  console.log("Tenants sorted alphabetically", tenants.value);
}

function sortAllTenants() {
  tenants.value.sort((x, y) => {
    if (x.sortOrder < y.sortOrder) {
      return -1;
    } else if (x.sortOrder > y.sortOrder) {
      return 1;
    }
    return 0;
  });
}

async function saveSortedTenants() {
  let orderToSave = {
    tenantOrders: tenants.value.map((t) => ({
      tenantId: t.tenantId,
      sortOrder: t.sortOrder,
    })),
  };

  popupError.value = await bldgStore.saveTenantOrder(orderToSave);
  if (popupError.value) return;

  tenantOrderChanged.value = false;
  loadData();
}

// Bulk Add Popup ---------------------------

let isBulkPopupVisible = ref(false);

const { handleSubmit: handleSubmitBulk, errors: errorsBulk } = useForm({
  validationSchema: yup.object({
    bulkTenantList: yup.string().required().label("Bulk Tenant List"),
  }),
  initialValues: {
    bulkTenantList: "",
  },
});

const { value: bulkTenantList } = useField<string>("bulkTenantList");

let saveBulkClick = handleSubmitBulk(onValidSubmitBulk, onInvalidSubmit);

async function onValidSubmitBulk() {
  let bulkTenantDTO = {
    propertyId: property.value?.propertyId,
    bulkTenantList: bulkTenantList.value,
  } as BulkTenantRequest;

  if (property.value && property.value.portalId) {
    popupError.value = await bldgStore.bulkSaveTenant(bulkTenantDTO);
    if (!popupError.value) {
      isBulkPopupVisible.value = false;
      await bldgStore.tryGetBuildingData(property.value.siteCode, true);
    }
  }
}

let bulkAddClick = function () {
  bulkTenantList.value = "";
  popupError.value = "";
  isBulkPopupVisible.value = true;
};
</script>
